import React, { useEffect, useState } from 'react';
import { Box, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchDrawingBase, updateDrawingBase } from 'store/actions/drawingBaseActions';
import {
  apiCheckDrawingBaseHasDrawings,
  apiCreatePrize,
  apiDeletePrize,
  apiDeletePrizeUpload,
  apiGetPrizeUploads,
  apiGetPrizes,
  apiUpdatePrize,
  apiUploadPrizeFile,
} from 'API/lotteryApi';
import type { ApiError } from 'API/apiClient';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { addAlert } from 'store/reducers/alertReducer';
import { PrizeType, type IPrizeUpload } from 'types/IPrize';
import { EMPTY_AMOUNT, amountInputToCents, centsToAmountInput, isValidAmountInput } from 'utility/amountInput';
import { DrawingBasePrizeRow, type PrizeRow } from './DrawingBasePrizeRows';

const columns: SortableColumn<PrizeRow>[] = [
  { name: 'Gewinnsumme', width: '55%' },
  { name: 'GewinnTyp', width: '35%' },
  { name: 'Aktionen', width: '1%' },
];

/** Must match the limit the upload endpoint sets in prizes.controller.ts. */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

let rowCounter = 0;
const emptyRow = (): PrizeRow => ({ key: `new-${(rowCounter += 1)}`, amount: EMPTY_AMOUNT, typ: PrizeType.GELDGEWINN, title: '', description: '', uploads: [], pendingFiles: [] });

export const DrawingBaseEditPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId, drawingBaseId } = useParams<{ lotteryId: string; drawingBaseId: string }>();
  const { selectedDrawingBase } = useAppSelector((state) => state.drawingBases);
  const base = Number(selectedDrawingBase?.id) === Number(drawingBaseId) ? selectedDrawingBase : null;
  const [hasDrawings, setHasDrawings] = useState(false);
  // Null until the field is touched, so the loaded name shows without an
  // effect that would overwrite whatever the user has typed.
  const [nameEdit, setNameEdit] = useState<string | null>(null);
  const name = nameEdit ?? base?.name ?? '';
  const [rows, setRows] = useState<PrizeRow[]>([]);
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [pageError, setPageError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const draggedKey = React.useRef<string | null>(null);

  const { columns: tableColumns, sort } = useTableSort(columns, rows, setRows);

  useEffect(() => {
    if (drawingBaseId) {
      dispatch(fetchDrawingBase(Number(drawingBaseId)));
    }
  }, [dispatch, drawingBaseId]);

  useEffect(() => {
    if (!drawingBaseId) return;

    let current = true;
    apiCheckDrawingBaseHasDrawings(Number(drawingBaseId))
      .then((result) => {
        if (current) setHasDrawings(result.hasDrawings);
      })
      .catch(() => {
        if (current) setHasDrawings(false);
      });

    return () => {
      current = false;
    };
  }, [drawingBaseId]);

  useEffect(() => {
    if (!drawingBaseId) return;

    let current = true;
    void (async () => {
      try {
        const prizes = await apiGetPrizes(Number(drawingBaseId));
        const withUploads = await Promise.all(
          prizes.map(async (prize) => {
            const uploads = prize.typ === PrizeType.SACHGEWINN ? await apiGetPrizeUploads(prize.id) : [];
            return {
              key: `prize-${prize.id}`,
              id: prize.id,
              amount: centsToAmountInput(prize.value),
              typ: prize.typ,
              title: prize.title ?? '',
              description: prize.description ?? '',
              uploads,
              pendingFiles: [],
            } satisfies PrizeRow;
          })
        );
        if (current) setRows(withUploads.length > 0 ? withUploads : [emptyRow()]);
      } catch (error) {
        if (current) setPageError((error as ApiError).message);
      }
    })();

    return () => {
      current = false;
    };
  }, [drawingBaseId]);

  const backToList = () => navigate(`/lottery/${lotteryId}/drawing-bases`);

  const reloadUploads = async (row: PrizeRow) => {
    if (row.id === undefined) return;
    const uploads = await apiGetPrizeUploads(row.id);
    setRows((current) => current.map((entry) => (entry.key === row.key ? { ...entry, uploads } : entry)));
  };

  const updateRow = (key: string, change: (row: PrizeRow) => PrizeRow) =>
    setRows((current) => current.map((entry) => (entry.key === key ? change(entry) : entry)));

  const removePendingFile = (row: PrizeRow, index: number) =>
    updateRow(row.key, (entry) => ({ ...entry, pendingFiles: entry.pendingFiles.filter((_, i) => i !== index) }));

  const upload = async (row: PrizeRow, file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      dispatch(addAlert({ message: `Die Datei ist zu groß (maximal ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB).`, severity: 'warning' }));
      return;
    }
    if (row.id === undefined) {
      updateRow(row.key, (entry) => ({ ...entry, pendingFiles: [...entry.pendingFiles, file] }));
      return;
    }
    try {
      await apiUploadPrizeFile(row.id, file);
      await reloadUploads(row);
    } catch (error) {
      dispatch(addAlert({ message: (error as ApiError).message, severity: 'warning' }));
    }
  };

  const removeUpload = async (row: PrizeRow, upload: IPrizeUpload) => {
    if (row.id === undefined) return;
    try {
      await apiDeletePrizeUpload(row.id, upload.id);
      await reloadUploads(row);
    } catch (error) {
      dispatch(addAlert({ message: (error as ApiError).message, severity: 'warning' }));
    }
  };

  const removeRow = (row: PrizeRow) => {
    if (row.id !== undefined) setRemovedIds((current) => [...current, row.id as number]);
    setRows((current) => {
      const left = current.filter((entry) => entry.key !== row.key);
      return left.length > 0 ? left : [emptyRow()];
    });
  };

  const addRowAfter = (row: PrizeRow) =>
    setRows((current) => {
      const index = current.findIndex((entry) => entry.key === row.key);
      const next = [...current];
      next.splice(index + 1, 0, { ...emptyRow(), amount: row.amount, typ: row.typ, title: row.title, description: row.description });
      return next;
    });

  const moveRow = (targetKey: string) => {
    const fromKey = draggedKey.current;
    draggedKey.current = null;
    if (!fromKey || fromKey === targetKey) return;

    setRows((current) => {
      const from = current.findIndex((entry) => entry.key === fromKey);
      const to = current.findIndex((entry) => entry.key === targetKey);
      if (from < 0 || to < 0) return current;

      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const amountErrorOf = (row: PrizeRow) => (isValidAmountInput(row.amount) ? undefined : 'Betrag wie 5000,00');

  const save = async () => {
    setPageError(null);
    setNameError(null);

    if (!name.trim()) {
      setNameError('Name ist erforderlich');
      return;
    }
    if (rows.some((row) => !isValidAmountInput(row.amount))) {
      setPageError('Bitte alle Beträge im Format 5000,00 angeben.');
      return;
    }

    setSaving(true);
    try {
      const result = await dispatch(updateDrawingBase({ id: Number(drawingBaseId), data: { name } }));
      if (!isFulfilled(result)) return;

      for (const id of removedIds) {
        await apiDeletePrize(id);
      }

      // The row order is the sort order, exactly as the old form submitted it.
      for (const [index, row] of rows.entries()) {
        const payload = {
          drawingBaseId: Number(drawingBaseId),
          typ: row.typ,
          value: amountInputToCents(row.amount),
          title: row.typ === PrizeType.SACHGEWINN ? row.title : '',
          description: row.typ === PrizeType.SACHGEWINN ? row.description : '',
          sort: index,
        };
        if (row.id === undefined) {
          const created = await apiCreatePrize(payload);
          if (row.typ === PrizeType.SACHGEWINN) {
            for (const file of row.pendingFiles) {
              await apiUploadPrizeFile(created.id, file);
            }
          }
        } else {
          await apiUpdatePrize(row.id, payload);
        }
      }

      backToList();
    } catch (error) {
      setPageError((error as ApiError).message);
    } finally {
      setSaving(false);
    }
  };

  if (!base) return null;

  if (hasDrawings) {
    return (
      <Box>
        <PageHeader title="Ziehungsbasis editieren" />
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          Diese Ziehungsbasis kann nicht bearbeitet werden, da Ziehungen darauf basieren.
        </Alert>
        <TextButton onClick={backToList}>Zurück</TextButton>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={`Ziehungsbasis für ${base.drawingType?.name || '-'}`} />

      <Box sx={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 2 }}>

        <Box sx={{ maxWidth: 600 }}>
          <CustomTextField name="name" label="Name" value={name} onChange={(event) => setNameEdit(event.target.value)} error={Boolean(nameError)} helperText={nameError ?? ''} />
        </Box>

        {pageError && <Alert severity="error">{pageError}</Alert>}

        <StyledTable columns={tableColumns} sort={sort}>
          {rows.map((row, index) => (
            <DrawingBasePrizeRow
              key={row.key}
              row={row}
              isLast={index === rows.length - 1}
              amountError={amountErrorOf(row)}
              onChange={(next) => setRows((current) => current.map((entry) => (entry.key === row.key ? next : entry)))}
              onAdd={() => addRowAfter(row)}
              onRemove={() => removeRow(row)}
              onUpload={(file) => void upload(row, file)}
              onRemoveUpload={(entry) => void removeUpload(row, entry)}
              onRemovePendingFile={(index) => removePendingFile(row, index)}
              onDragStart={() => {
                draggedKey.current = row.key;
              }}
              onDropOn={() => moveRow(row.key)}
            />
          ))}
        </StyledTable>

        <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
          <RedButton onClick={() => void save()} disabled={saving}>
            Speichern
          </RedButton>
          <TextButton onClick={backToList}>Abbrechen</TextButton>
        </Box>
      </Box>
    </Box>
  );
};
